import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import Modal, { ModalProps } from '@cineamo/ui/src/modals/Modal';

import { BuyerDetails } from './actions';

export type EmailInputModalProps = Omit<ModalProps, 'onSubmit'> & {
    onSubmit: (buyer: BuyerDetails) => void;
    isLoading?: boolean;
    websiteStyle?: WebsiteStyle & WebsiteInputStyle;
    /** What the signed-in account already knows, so a buyer only fills in what is missing. */
    defaultValues?: Partial<BuyerDetails>;
    /** Set when the API rejected the address, e.g. a domain that cannot receive mail (CIN-15349). */
    emailError?: string;
    onEmailChange?: () => void;
};

const INPUT_CLASS_NAME =
    'w-full h-[48px] bg-[var(--input-background-color)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--focus-ring-color)] text-[var(--input-text-color)] text-body-16 rounded-[4px] px-[16px] border-none placeholder:text-[var(--input-placeholder-color)]';

const EmailInputModal: React.FC<EmailInputModalProps> = ({
    onSubmit,
    isLoading = false,
    websiteStyle,
    defaultValues,
    emailError,
    onEmailChange,
    ...modalProps
}) => {
    const t = useTranslations();

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const [email, setEmail] = useState(defaultValues?.email ?? '');
    const [firstName, setFirstName] = useState(defaultValues?.firstName ?? '');
    const [lastName, setLastName] = useState(defaultValues?.lastName ?? '');
    const [isValidEmail, setIsValidEmail] = useState(validateEmail(defaultValues?.email ?? ''));

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        setIsValidEmail(validateEmail(newEmail));
        onEmailChange?.();
    };

    // CIN-15320: both name parts are obligatory. Whitespace alone is not a name, so the submit button
    // stays disabled for it rather than sending a blank the API would store as null.
    const isValidName = firstName.trim() !== '' && lastName.trim() !== '';
    const canSubmit = isValidEmail && isValidName && !isLoading;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (canSubmit) {
            onSubmit({ email, firstName: firstName.trim(), lastName: lastName.trim() });
        }
    };

    return (
        <Modal
            {...modalProps}
            className="h-min"
            title={t('tickets.payment.email-input.title')}
            onClose={isLoading ? undefined : modalProps.onClose}>
            <div
                style={
                    {
                        '--input-background-color':
                            websiteStyle?.inputBackgroundColorOnSurface || websiteStyle?.inputBackgroundColor,
                        '--input-text-color': websiteStyle?.inputTextColorOnSurface || websiteStyle?.inputTextColor,
                        '--input-placeholder-color':
                            websiteStyle?.inputPlaceholderColorOnSurface || websiteStyle?.inputPlaceholderColor
                    } as React.CSSProperties
                }>
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col space-y-24">
                    <div className="flex flex-col space-y-12">
                        <p className="text-body-14">{t('tickets.payment.email-input.explanation')}</p>

                        <div className="flex flex-col space-y-12 lg:flex-row lg:space-y-0 lg:space-x-12">
                            <div className="flex flex-col space-y-4 lg:flex-1">
                                <label
                                    htmlFor="first-name-modal"
                                    className="text-body-12 opacity-50">
                                    {t('tickets.payment.email-input.first-name')} *
                                </label>
                                <input
                                    id="first-name-modal"
                                    type="text"
                                    autoComplete="given-name"
                                    maxLength={100}
                                    className={INPUT_CLASS_NAME}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="flex flex-col space-y-4 lg:flex-1">
                                <label
                                    htmlFor="last-name-modal"
                                    className="text-body-12 opacity-50">
                                    {t('tickets.payment.email-input.last-name')} *
                                </label>
                                <input
                                    id="last-name-modal"
                                    type="text"
                                    autoComplete="family-name"
                                    maxLength={100}
                                    className={INPUT_CLASS_NAME}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col space-y-4">
                            <label
                                htmlFor="email-modal"
                                className="text-body-12 opacity-50">
                                {t('tickets.payment.email-input.email-address')} *
                            </label>
                            <input
                                id="email-modal"
                                type="email"
                                autoComplete="email"
                                maxLength={175}
                                className={INPUT_CLASS_NAME}
                                placeholder="example@example.com"
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isLoading}
                                aria-invalid={!!emailError}
                                aria-describedby={emailError ? 'email-modal-error' : undefined}
                            />
                            {emailError && (
                                <p
                                    id="email-modal-error"
                                    className="text-body-12 text-red">
                                    {emailError}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-12">
                        {!isLoading && (
                            <Button
                                type="button"
                                style={ButtonStyle.default}
                                onClick={modalProps.onClose ?? undefined}>
                                {t('common.back')}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            style={ButtonStyle.default}
                            disabled={!canSubmit}
                            leadingIcon={isLoading && <IconLoading16 className="animate-spin" />}>
                            {isLoading ? t('tickets.loading') : t('tickets.continue')}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default EmailInputModal;
